import { LeasingCalculatorSharedFormProps } from 'modules/leasingCalculator/components/common/LeasingCalculatorData';
import {
  Button,
  ButtonType,
  InputWithLabel,
  ParagraphTitle,
  SelectWithLabel,
} from 'modules/ui';
import { useForm } from 'modules/ui/form/useForm';
import { SufixType } from 'modules/ui/input/common/SufixType';
import { SliderWithLabel } from 'modules/ui/slider/SliderWithLabel';
import { parseFromCurrencyString, parseToCurrencyString } from 'modules/utils';
import { Trans, useTranslation } from 'react-i18next';
import { UwtType } from 'modules/types/LeasingCalculatorCodes';
import { LeasingDataContainer } from '../../styled';
import { useLeasingCalculatorCodes } from '../../../../../hooks/codesHooks/useLeasingCalculatorCodes';

const PAYMENT_PERIOD_UWT_TYPES: UwtType[] = [
  { id: 'Mjesečno', name: 'Mjesečno' },
  { id: 'Kvartalno', name: 'Kvartalno' },
  { id: 'Polugodišnje', name: 'Polugodišnje' },
  { id: 'Godišnje', name: 'Godišnje' },
];

const PAYMENT_PERIOD_OPTIONS = PAYMENT_PERIOD_UWT_TYPES.map((t) => ({
  label: t.name,
  value: t.id,
}));

export const GeneralLeasingData: React.FC<LeasingCalculatorSharedFormProps> = ({
  defaultValues,
  onChange,
}) => {
  const { t } = useTranslation();
  const { financingPeriods } = useLeasingCalculatorCodes();

  const { updateFormData, updateSelectData } = useForm({
    onChange,
  });

  const ppmvTooltip = (
    <Trans
      i18nKey="leasingCalculator.infoCalculation.generalData.ppmvTooltip"
      components={{
        calcLink: (
          <Button
            type={ButtonType.Text}
            text={t(
              'leasingCalculator.infoCalculation.generalData.ppmvTooltipButtonText',
            )}
            onClick={() =>
              window.open(
                'https://carina.gov.hr/pristup-informacijama/propisi-i-sporazumi/trosarinsko-postupanje/dodatne-informacije-o-trosarinama-i-posebnim-porezima/posebni-porez-na-motorna-vozila-3650/informativni-kalkulator-za-izracun-ppmv-a/4838',
                '_blank',
              )
            }
          />
        ),
      }}
    />
  );

  return (
    <LeasingDataContainer marginTop="0">
      <ParagraphTitle>
        {t('leasingCalculator.infoCalculation.generalData.title')}
      </ParagraphTitle>

      <SliderWithLabel
        label={t('leasingCalculator.infoCalculation.generalData.netPrice')}
        min={0}
        max={250000}
        defaultValue={defaultValues?.leasingNetObjectPrice}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={(value) =>
          updateFormData(
            'leasingNetObjectPrice',
            parseFromCurrencyString(value),
          )
        }
      />
      <InputWithLabel
        label={t('leasingCalculator.infoCalculation.generalData.ppmv')}
        labelTooltip={ppmvTooltip}
        placeholder={t(
          'leasingCalculator.infoCalculation.generalData.ppmvPlaceholder',
        )}
        parser={(value) => value && parseToCurrencyString(value)}
        defaultValue={defaultValues?.leasingObjectPpmv}
        onBlur={(e) =>
          updateFormData(
            'leasingObjectPpmv',
            parseFromCurrencyString(e.target.value),
          )
        }
        sufix={SufixType.Euro}
      />
      <SelectWithLabel
        searchable={false}
        label={t('leasingCalculator.infoCalculation.generalData.paymentPeriod')}
        options={PAYMENT_PERIOD_OPTIONS}
        placeholderText={t(
          'leasingCalculator.infoCalculation.generalData.paymentPeriodPlaceholder',
        )}
        defaultValue={defaultValues?.leasingPaymentPeriodType?.id}
        onChange={(value) =>
          updateSelectData(
            'leasingPaymentPeriodType',
            value,
            PAYMENT_PERIOD_UWT_TYPES,
          )
        }
      />
      <SelectWithLabel
        searchable={false}
        label={t(
          'leasingCalculator.infoCalculation.generalData.financingPeriod',
        )}
        options={financingPeriods.options || []}
        placeholderText={t(
          'leasingCalculator.infoCalculation.generalData.financingPeriodPlaceholder',
        )}
        defaultValue={defaultValues?.financingPeriod?.id}
        onChange={(value) =>
          updateSelectData(
            'financingPeriod',
            value,
            financingPeriods.uwtTypes || [],
          )
        }
      />
      <SliderWithLabel
        label={t('leasingCalculator.infoCalculation.generalData.interestRate')}
        min={0}
        max={10}
        valueType={SufixType.Percentage}
        defaultValue={defaultValues?.infoInterestRate}
        parser={(value) => value && parseToCurrencyString(value)}
        onChange={(value) =>
          updateFormData('infoInterestRate', parseFromCurrencyString(value))
        }
      />
    </LeasingDataContainer>
  );
};
